import clone from 'clone';
import 'custom-event-polyfill';
import { eventContextChanged } from './cam-context-switcher-constants';
import userIcon from '../../assets/images/icons/user.svg';
import usersIcon from '../../assets/images/icons/users.svg';
import workspaceIcon from '../../assets/images/icons/workspace.svg';

let _currentState = new Map();

/**
 * This module should only ever be imported using the default singleton export.
 * This will ensure that all components importing the datastore have the same
 * instance of the datastore.
 *
 * The class is exported only to enable testing
 */
export class CamContextSwitcherDatastore {

  constructor() {
    _currentState.set('userName', null);
    _currentState.set('selectedOrganization', null);
    _currentState.set('selectedCostcenter', null);
    _currentState.set('selectedWorkspace', null);
    _currentState.set('personalWorkspace', null);
    _currentState.set('personalWorkspaceResponse', null);
    _currentState.set('scopesResponse', null);
    _currentState.set('filter', null);
    _currentState.set('favorites', null);
    _currentState.set('allContexts', null);
    _currentState.set('currentConfig', null);
    _currentState.set('selectedTab', 'workspace');
  }

  initializeData(userName, scopesResponse, personalWorkspaceResponse, context, config) {
    _currentState.set('currentConfig', config);

    this.apiUrl = config.apiUrl.replace('/+$', '');
    _currentState.set('userName', userName);
    _currentState.set('scopesResponse', scopesResponse);

    this.updatePersonalWorkspace(personalWorkspaceResponse);

    if(this._canSelectContext(context)) {
      this.selectedTab = context.type;
      this.changeContext(context);
    } else if (_currentState.get('personalWorkspace')) {
      this.selectedTab = 'workspace';
      this.changeContext(this.personalWorkspace);
    }
    _currentState.set('allContexts', clone(this.workspaces).concat(this.organizations).concat(this.costcenters));
  }

  updatePersonalWorkspace(personalWorkspaceResponse) {
    _currentState.set('personalWorkspaceResponse', personalWorkspaceResponse);
    _currentState.set('personalWorkspace', _currentState.get('scopesResponse').workspaces.find((ws) => {
      return ws.id === _currentState.get('personalWorkspaceResponse').id;
    }));
    _currentState.set('favorites', _currentState.get('personalWorkspaceResponse').favorites);
  }

  fireContextChangedEvent() {
    let eventOptions = {
      detail: {
        organization: this.selectedOrganization,
        costcenter: this.selectedCostcenter,
        workspace: this.selectedWorkspace
      },
      bubbles: true,
      cancelable: false
    };

    let camContextSwitcher = document.querySelector('#cam-context-switcher');
    camContextSwitcher.dispatchEvent(new CustomEvent(eventContextChanged, eventOptions));
  }

  _isFavorite(id, type) {
    return _currentState.get('favorites').findIndex((f) => {
      return f.id === id && f.type === type;
    }) !== -1;
  }

  _canSelectContext(context) {
    let canSelect = false;
    if(!context || !context.type || !context.id) {
      return false;
    } else if(this.config.enabledContexts.includes(context.type.toLowerCase())) {
      let contexts;
      switch(context.type) {
      case 'workspace':
        contexts = _currentState.get('scopesResponse').workspaces;
        break;
      case 'costcenter':
        contexts = _currentState.get('scopesResponse').costcenters;
        break;
      case 'organization':
        contexts = _currentState.get('scopesResponse').organizations;
        break;
      default:
        contexts = [];
      }
      canSelect = contexts.findIndex((c) => c.id === context.id) !== -1;
    }
    return canSelect;
  }

  updateAllContexts() {
    _currentState.set('allContexts', clone(this.workspaces).concat(this.organizations).concat(this.costcenters));
  }

  get config(){
    return clone(_currentState.get('currentConfig'));
  }

  get selectedContext(){
    let display;
    let organization = this.selectedOrganization;
    let costcenter = this.selectedCostcenter;
    let workspace = this.selectedWorkspace;
    let context;
    if (workspace) {
      display = this.displayWorkspace(workspace);
      display.parent = workspace.costcenter_name;
      display.grandparent = workspace.owning_organization.name;
      context = {
        display: display,
        context: workspace
      };
    } else if (costcenter) {
      display = this.displayCostcenter(costcenter);
      display.parent = costcenter.owning_organization.name;
      context = {
        display: display,
        context: costcenter
      };
    } else if (organization) {
      display = this.displayOrganization(organization);
      context = {
        display: display,
        context: organization
      };
    } else {
      display = {
        title: 'No Context Selected',
        typeName: 'Workspace'
      };
      context = {
        display: display,
        context: null
      };
    }

    return context;
  }

  get favorites() {
    if (_currentState.get('personalWorkspaceResponse') === null) {
      return [];
    }
    let favorites = clone(_currentState.get('favorites'));
    let favoriteContexts = [];
    let allContexts = clone(_currentState.get('allContexts'));

    favorites.forEach((favorite) => {
      let foundContext = allContexts.find((currentContext) => currentContext.context.id === favorite.id && currentContext.context.type === favorite.type);
      if(foundContext){
        favoriteContexts.push(foundContext);
      }
    });

    if (this.selectedTab === 'favorite') {
      favoriteContexts = this._applyFilter(favoriteContexts);
    }

    return favoriteContexts;
  }

  get personalWorkspace() {
    return clone(_currentState.get('personalWorkspace'));
  }

  get personalWorkspaceResponse() {
    return clone(_currentState.get('personalWorkspaceResponse'));
  }

  set filter(filter) {
    filter = filter ? filter.trim() : filter;
    _currentState.set('filter', filter);
    this.updateAllContexts();
  }

  get filter() {
    return clone(_currentState.get('filter'));
  }

  get organizations() {
    if (_currentState.get('scopesResponse') === null) {
      return [];
    }
    let displayOrganizationList = this._sortByAttr(_currentState.get('scopesResponse').organizations, 'name');

    displayOrganizationList = displayOrganizationList.map((org) => {
      return {
        context: org,
        display: this.displayOrganization(org)
      };
    });

    if (this.selectedTab === 'organization') {
      displayOrganizationList = this._applyFilter(displayOrganizationList);
    }

    return displayOrganizationList;
  }

  get costcenters() {
    if (_currentState.get('scopesResponse') === null) {
      return [];
    }
    let organizationId = _currentState.get('selectedOrganization') ? _currentState.get('selectedOrganization').id : null;

    let filteredCostcenterList = _currentState.get('scopesResponse').costcenters;
    if (organizationId !== null) {
      filteredCostcenterList = filteredCostcenterList.filter((cc) => {
        return cc.organizations.includes(organizationId);
      });
    }
    filteredCostcenterList = this._sortByAttr(filteredCostcenterList, 'name');

    let displayCostcenterList = filteredCostcenterList.map((cc) => {
      return {
        context: cc,
        display: this.displayCostcenter(cc)
      };
    });

    if (this.selectedTab === 'costcenter') {
      displayCostcenterList = this._applyFilter(displayCostcenterList);
    }

    return displayCostcenterList;
  }

  get workspaces() {
    if (_currentState.get('scopesResponse') === null) {
      return [];
    }
    let organizationId = _currentState.get('selectedOrganization') ? _currentState.get('selectedOrganization').id : null;
    let costcenterId = _currentState.get('selectedCostcenter') ? _currentState.get('selectedCostcenter').id : null;

    let filteredWorkspaceList = [ _currentState.get('personalWorkspace') ].concat(
      this._sortByAttr(_currentState.get('scopesResponse').workspaces
        .filter((ws) => ws.type !== 'personal_workspace'),
      'name')
    );
    if (organizationId !== null) {
      filteredWorkspaceList = filteredWorkspaceList.filter((ws) => {
        return ws.organization ? ws.organization === organizationId : ws.organizations.includes(organizationId);
      });
    }
    if (costcenterId !== null) {
      filteredWorkspaceList = filteredWorkspaceList.filter((ws) => {
        return ws.costcenter === costcenterId;
      });
    }

    let displayWorkspaceList = [];
    displayWorkspaceList = displayWorkspaceList.concat(filteredWorkspaceList.map((ws) => {
      return {
        context: ws,
        display: this.displayWorkspace(ws)
      };
    }));

    if (this.selectedTab === 'workspace') {
      displayWorkspaceList = this._applyFilter(displayWorkspaceList);
    }

    return displayWorkspaceList;
  }

  _matchToken(item, token) {
    return (
      item.display.title.toLowerCase().includes(token)
      || item.context.id.toLowerCase().includes(token)
      || (item.context.costcenter_name && item.context.costcenter_name.toLowerCase().includes(token))
      || (item.context.owning_organization && item.context.owning_organization.name && item.context.owning_organization.name.toLowerCase().includes(token))
      || (item.context.organizations && item.context.organizations.some((i) => i.toLowerCase().includes(token)))
      || (item.context.costcenter && item.context.costcenter.toLowerCase().includes(token))
    );
  }

  _matchTokens(collection, tokens) {
    if (collection.length === 0 || tokens.length === 0) {
      return collection;
    }
    let token = tokens.shift().toLowerCase();
    collection = collection.filter((item) => this._matchToken(item, token));
    return this._matchTokens(collection, tokens);
  }

  _applyFilter(displayContextList) {
    if (!this.filter) {
      return displayContextList;
    }
    let tokens = this.filter.trim().split(/\s+/);

    return this._matchTokens(displayContextList, tokens);
  }

  displayOrganization(organization) {
    return {
      title: organization.name,
      iconUrl: this._formatIconUrl(organization.icon, 'organization'),
      selected: _currentState.get('selectedOrganization') !== null ? organization.id === _currentState.get('selectedOrganization').id : organization.id === null,
      favorite: this._isFavorite(organization.id, organization.type),
      typeName: 'Organization'
    };
  }

  displayCostcenter(costcenter) {
    return {
      title: costcenter.name,
      selected: _currentState.get('selectedCostcenter') !== null ? costcenter.id === _currentState.get('selectedCostcenter').id : costcenter.id === null,
      badge: costcenter.owning_organization.id,
      favorite: this._isFavorite(costcenter.id, costcenter.type),
      memberIcon: workspaceIcon,
      memberCount: costcenter.members.workspaces,
      typeName: 'Cost Center'
    };
  }

  displayWorkspace(workspace) {
    let iconType = 'workspace';
    let isPersonal = false;
    if (workspace.id === _currentState.get('personalWorkspace').id) {
      iconType = 'user';
      isPersonal = true;
    }
    let memberCount = isPersonal ? null : (workspace.members.workspaces || 0);
    return {
      title: isPersonal ? 'My Workspace' : workspace.name,
      iconUrl: this._formatIconUrl(workspace.icon, iconType),
      selected: _currentState.get('selectedWorkspace') && workspace.id === _currentState.get('selectedWorkspace').id,
      favorite: this._isFavorite(workspace.id, workspace.type),
      personal: isPersonal,
      badge: workspace.owning_organization.id,
      subtitle: workspace.costcenter_name,
      memberIcon: memberCount > 1 ? usersIcon : userIcon,
      memberCount: memberCount,
      typeName: 'Workspace'
    };
  }

  _formatIconUrl(relativePath, iconType) {
    var imageSource;
    if (!relativePath || relativePath === null) {
      let defaultIcon;
      switch (iconType) {
      case 'user':
        defaultIcon = '/images/user-nophoto-lg.png'; break;
      case 'workspace':
        defaultIcon = '/images/workspace-nophoto-lg.png'; break;
      case 'organization':
        defaultIcon = '/images/org-nophoto.png'; break;
      default:
        defaultIcon = '/images/workspace-nophoto-lg.png'; break;
      }
      imageSource = this.apiUrl + defaultIcon;
    } else if (relativePath === 'none') {
      imageSource = '';
    } else if (relativePath.match(/^http:\/\//) || relativePath.match(/^https:\/\//)) {
      imageSource = relativePath;
    } else {
      if (relativePath.match(/^\//)) {
        imageSource = this.apiUrl + relativePath;
      } else {
        imageSource = this.apiUrl + '/' + relativePath;
      }
    }
    return imageSource;
  }

  _sortByAttr(unsortedArray, attrName) {
    let sortedArray = unsortedArray.sort((a, b) => {
      let aAttr = a[attrName].toLowerCase();
      let bAttr = b[attrName].toLowerCase();
      return (aAttr < bAttr) ? -1 : ((aAttr > bAttr) ? 1 : 0);
    });
    return sortedArray;
  }

  get selectedOrganization() {
    return clone(_currentState.get('selectedOrganization'));
  }

  get selectedCostcenter() {
    return clone(_currentState.get('selectedCostcenter'));
  }

  get selectedWorkspace() {
    return clone(_currentState.get('selectedWorkspace'));
  }

  changeFilterScope(scope){
    if (scope.type === 'organization'){
      this.changeOrganization(scope.id);
    } else if(scope.type === 'costcenter'){
      this.changeCostcenter(scope.id);
    } else {
      this.changeWorkspace(scope.id);
    }
  }

  changeOrganization(organizationId) {
    let updatedOrganization = _currentState.get('scopesResponse').organizations.find((org) => {
      return org.id === organizationId;
    }) || null ;
    if (_currentState.get('selectedOrganization') && organizationId !== _currentState.get('selectedOrganization').id) {
      this.changeCostcenter(null);
    }
    _currentState.set('selectedOrganization', updatedOrganization);
  }

  changeCostcenter(costcenterId) {
    let updatedCostcenter = _currentState.get('scopesResponse').costcenters.find((cc) => {
      return cc.id === costcenterId;
    }) || null;
    _currentState.set('selectedCostcenter', updatedCostcenter);
  }

  changeWorkspace(workspaceId) {
    if (_currentState.get('selectedWorkspace') && workspaceId === _currentState.get('selectedWorkspace')) {
      return;
    }
    let updatedWorkspace = _currentState.get('scopesResponse').workspaces.find((ws) => {
      return ws.id === workspaceId;
    }) || null;
    _currentState.set('selectedWorkspace', updatedWorkspace);
  }

  changeContext(context) {
    if (context.type === 'organization') {
      this.changeOrganization(context.id);
      this.changeCostcenter(null);
      this.changeWorkspace(null);
    } else if (context.type === 'costcenter') {
      this.changeCostcenter(context.id);
      this.changeWorkspace(null);
    } else if (context.type.includes('workspace')) {
      this.changeWorkspace(context.id);
    }
    this.filter = null;
    this.fireContextChangedEvent();
  }

  get userName() {
    return clone(_currentState.get('userName'));
  }

  get selectedTab() {
    return _currentState.get('selectedTab');
  }

  set selectedTab(tabName) {
    _currentState.set('selectedTab', tabName);
  }
}

export default new CamContextSwitcherDatastore();



// WEBPACK FOOTER //
// ./src/cam-context-switcher/scripts/cam-context-switcher-datastore.js